import type { AiAllowanceSummary, AssistantSuggestion, AssistantSuggestionConfidence, AssistantSuggestionKind, WorkspacePlanId } from '~/types/models';

export type AiWorkload =
    | 'thread_analysis'
    | 'prompt_suggestions'
    | 'prompt_chat'
    | 'suggestion_refinement'
    | 'email_context';

export type { AiAllowanceSummary };

export const AI_POLICY = {
    promptMaxChars: 2_000,
    historyMaxTurns: 4,
    historyItemMaxChars: 2_000,
    historyTotalMaxChars: 6_000,
    refinementMaxChars: 500,
    relatedSuggestionsMax: 10,
    handledMessageIdsMax: 100,
    messageContentMaxChars: 4_000,
    messageContextMaxChars: 800,
    subjectMaxChars: 300,
    emailBodyMaxChars: 8_000,
    sourceMessageIdsMax: 20,
    suggestionsMax: 8,
    providerMessagesMax: 12,
    providerInputMaxChars: 100_000,
    suggestionTitleMaxChars: 140,
    suggestionSummaryMaxChars: 700,
    suggestionRationaleMaxChars: 700,
    suggestionReplyMaxChars: 2_000,
    suggestionProjectNameMaxChars: 140,
    suggestionDescriptionMaxChars: 1_500,
    suggestionTaskTitleMaxChars: 180,
    perUserRequestsPerMinute: 5,
    perUserRequestsPerDay: 50,
    requestTimeoutMs: 25_000,
} as const;

const monthlyRequestLimits: Record<WorkspacePlanId, number> = {
    starter: 200,
    growth: 1_000,
    pro: 3_000,
};

const outputTokenLimits: Record<AiWorkload, number> = {
    thread_analysis: 1_600,
    prompt_suggestions: 1_600,
    prompt_chat: 800,
    suggestion_refinement: 900,
    email_context: 220,
};

const suggestionKinds = new Set<AssistantSuggestionKind>([
    'new_project',
    'new_task',
    'reply_draft',
    'follow_up',
    'quote_review',
    'risk_flag',
]);

const suggestionConfidences = new Set<AssistantSuggestionConfidence>([
    'low',
    'medium',
    'high',
]);

export class AiPolicyError extends Error {
    readonly code: string;
    readonly statusCode: number;

    constructor(message: string, code = 'ai_request_invalid', statusCode = 400) {
        super(message);
        this.name = 'AiPolicyError';
        this.code = code;
        this.statusCode = statusCode;
    }
}

function requiredText(value: unknown, label: string, maxChars: number): string {
    const normalized = String(value || '').trim();
    if (!normalized) {
        throw new AiPolicyError(`${label} is required.`);
    }
    if (normalized.length > maxChars) {
        throw new AiPolicyError(
            `${label} must be ${maxChars.toLocaleString()} characters or fewer.`,
            'ai_input_too_large',
            413,
        );
    }
    return normalized;
}

function optionalText(value: unknown, maxChars: number, truncate: boolean): string | undefined {
    const normalized = String(value || '').trim();
    if (!normalized) return undefined;
    if (normalized.length <= maxChars) return normalized;
    if (truncate) return normalized.slice(0, maxChars).trim();
    throw new AiPolicyError(
        `Assistant suggestion content must be ${maxChars.toLocaleString()} characters or fewer.`,
        'ai_input_too_large',
        413,
    );
}

export function truncateAiText(value: unknown, maxChars: number): string {
    return String(value || '').trim().slice(0, maxChars).trim();
}

/**
 * Removes common credentials and high-risk identifiers before untrusted
 * workspace content is included in a provider request. This is deliberately
 * conservative and does not alter the source record stored by Vuey.
 */
export function redactAiSensitiveText(value: unknown): string {
    return String(value || '')
        .replace(/-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/gi, '[private key removed]')
        .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]{16,}/gi, 'Bearer [token removed]')
        .replace(/\b(?:sk|rk|pk)_[A-Za-z0-9_-]{16,}\b/g, '[credential removed]')
        .replace(/\b(password|passcode|secret|api[_ -]?key)\s*[:=]\s*\S+/gi, '$1: [secret removed]')
        .replace(/\b(?:\d[ -]*?){13,19}\b/g, '[payment number removed]');
}

export function prepareAiText(value: unknown, maxChars: number): string {
    return truncateAiText(redactAiSensitiveText(value), maxChars);
}

export function normalizeAiPrompt(
    prompt: unknown,
    history: unknown,
): {
    prompt: string;
    history: Array<{ role: 'user' | 'assistant'; content: string }>;
} {
    const normalizedPrompt = requiredText(prompt, 'Prompt', AI_POLICY.promptMaxChars);
    const historyItems = Array.isArray(history) ? history.slice(-AI_POLICY.historyMaxTurns) : [];
    const normalizedHistory = historyItems
        .map((item) => {
            const value = item && typeof item === 'object'
                ? item as { role?: unknown; content?: unknown }
                : {};
            const content = String(value.content || '').trim();
            if (!content) return null;
            if (content.length > AI_POLICY.historyItemMaxChars) {
                throw new AiPolicyError(
                    `Each assistant history item must be ${AI_POLICY.historyItemMaxChars.toLocaleString()} characters or fewer.`,
                    'ai_input_too_large',
                    413,
                );
            }
            return {
                role: value.role === 'assistant' ? 'assistant' as const : 'user' as const,
                content,
            };
        })
        .filter((item): item is { role: 'user' | 'assistant'; content: string } => Boolean(item));

    const totalHistoryChars = normalizedHistory.reduce((sum, item) => sum + item.content.length, 0);
    if (totalHistoryChars > AI_POLICY.historyTotalMaxChars) {
        throw new AiPolicyError(
            `Assistant history must be ${AI_POLICY.historyTotalMaxChars.toLocaleString()} total characters or fewer.`,
            'ai_input_too_large',
            413,
        );
    }

    return {
        prompt: normalizedPrompt,
        history: normalizedHistory,
    };
}

export function normalizeHandledMessageIds(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return [...new Set(value
        .slice(0, AI_POLICY.handledMessageIdsMax)
        .map(item => String(item || '').trim())
        .filter(Boolean))];
}

export function normalizeAssistantSuggestion(
    value: unknown,
    options: {
        truncate?: boolean;
        validSourceMessageIds?: Set<string>;
        validProjectIds?: Set<string>;
        fallbackId?: string;
    } = {},
): AssistantSuggestion {
    if (!value || typeof value !== 'object') {
        throw new AiPolicyError('Assistant suggestion is invalid.');
    }

    const source = value as Record<string, unknown>;
    const kind = String(source.kind || '') as AssistantSuggestionKind;
    const confidence = String(source.confidence || '') as AssistantSuggestionConfidence;

    if (!suggestionKinds.has(kind)) {
        throw new AiPolicyError('Assistant suggestion kind is invalid.');
    }
    if (!suggestionConfidences.has(confidence)) {
        throw new AiPolicyError('Assistant suggestion confidence is invalid.');
    }

    const sourceMessageIds = [...new Set(
        (Array.isArray(source.sourceMessageIds) ? source.sourceMessageIds : [])
            .slice(0, AI_POLICY.sourceMessageIdsMax)
            .map(id => String(id || '').trim())
            .filter(Boolean)
            .filter(id => !options.validSourceMessageIds || options.validSourceMessageIds.has(id)),
    )];
    const projectId = optionalText(source.projectId, 160, Boolean(options.truncate));
    const validatedProjectId = projectId && (!options.validProjectIds || options.validProjectIds.has(projectId))
        ? projectId
        : undefined;

    return {
        id: optionalText(source.id, 160, true) || options.fallbackId || '',
        kind,
        title: requiredText(
            options.truncate
                ? truncateAiText(source.title, AI_POLICY.suggestionTitleMaxChars)
                : source.title,
            'Assistant suggestion title',
            AI_POLICY.suggestionTitleMaxChars,
        ),
        summary: requiredText(
            options.truncate
                ? truncateAiText(source.summary, AI_POLICY.suggestionSummaryMaxChars)
                : source.summary,
            'Assistant suggestion summary',
            AI_POLICY.suggestionSummaryMaxChars,
        ),
        rationale: requiredText(
            options.truncate
                ? truncateAiText(source.rationale, AI_POLICY.suggestionRationaleMaxChars)
                : source.rationale,
            'Assistant suggestion rationale',
            AI_POLICY.suggestionRationaleMaxChars,
        ),
        confidence,
        topicKey: optionalText(source.topicKey, 160, Boolean(options.truncate)),
        topicLabel: optionalText(source.topicLabel, 160, Boolean(options.truncate)),
        sourceMessageIds,
        suggestedReply: optionalText(source.suggestedReply, AI_POLICY.suggestionReplyMaxChars, Boolean(options.truncate)),
        suggestedProjectName: optionalText(source.suggestedProjectName, AI_POLICY.suggestionProjectNameMaxChars, Boolean(options.truncate)),
        suggestedProjectDescription: optionalText(source.suggestedProjectDescription, AI_POLICY.suggestionDescriptionMaxChars, Boolean(options.truncate)),
        suggestedTaskTitle: optionalText(source.suggestedTaskTitle, AI_POLICY.suggestionTaskTitleMaxChars, Boolean(options.truncate)),
        suggestedTaskDescription: optionalText(source.suggestedTaskDescription, AI_POLICY.suggestionDescriptionMaxChars, Boolean(options.truncate)),
        projectId: validatedProjectId,
    };
}

export function normalizeAiRefinement(
    suggestion: unknown,
    relatedSuggestions: unknown,
    instructions: unknown,
): {
    suggestion: AssistantSuggestion;
    relatedSuggestions: AssistantSuggestion[];
    instructions: string;
} {
    const normalizedInstructions = requiredText(
        instructions,
        'Refinement instructions',
        AI_POLICY.refinementMaxChars,
    );
    const normalizedSuggestion = normalizeAssistantSuggestion(suggestion);
    const related = Array.isArray(relatedSuggestions)
        ? relatedSuggestions.slice(0, AI_POLICY.relatedSuggestionsMax)
        : [];

    if (Array.isArray(relatedSuggestions) && relatedSuggestions.length > AI_POLICY.relatedSuggestionsMax) {
        throw new AiPolicyError(
            `Related suggestions are limited to ${AI_POLICY.relatedSuggestionsMax}.`,
            'ai_input_too_large',
            413,
        );
    }

    return {
        suggestion: normalizedSuggestion,
        relatedSuggestions: related.map(item => normalizeAssistantSuggestion(item)),
        instructions: normalizedInstructions,
    };
}

export function getAiMonthlyRequestLimit(planId: WorkspacePlanId): number {
    return monthlyRequestLimits[planId];
}

export function getAiOutputTokenLimit(workload: AiWorkload): number {
    return outputTokenLimits[workload];
}

export function calculateAiCostMicroUsd(model: string, inputTokens: number, outputTokens: number): number {
    const normalizedModel = model.toLowerCase();
    const pricing = normalizedModel.includes('gpt-5.4-nano')
        ? { input: 0.2, output: 1.25 }
        : normalizedModel.includes('gpt-5.4-mini')
            ? { input: 0.75, output: 4.5 }
            : null;

    if (!pricing) return 0;
    return Math.round((Math.max(0, inputTokens) * pricing.input) + (Math.max(0, outputTokens) * pricing.output));
}

export function getAiMonthResetIso(now = new Date()): string {
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString();
}
